import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import settingsService from '../services/settingsService';
import {
  cacheSettings,
  createInitialSettings,
  normalizeSettings,
} from './settingsContextUtils';
import { propagateGeneralSettingsToModules } from './generalSettingsBridge';
import i18n from '../../i18n';

const sg = (key, opts) => i18n.t(`serviceMessages.settings.${key}`, opts);

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error(sg('hook_outside_provider'));
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const defaultConfiguration = useMemo(
    () => ({
      documentPrefixes: {
        invoice: 'FAC',
        proforma: 'PRO',
        quote: 'DEV',
        quoteRequest: 'DD',
        order: 'BC',
        delivery: 'BL',
        reception: 'BR',
        return: 'RT',
        sendSlip: 'BE',
        generic: 'DOC',
      },
      entityPrefixes: {
        client: 'CLI',
        supplier: 'FRN',
        product: 'PRD',
        service: 'SRV',
        category: 'CAT',
        variant: 'VAR',
        unitOfMeasure: 'UOM',
        costMethod: 'CM',
        priceList: 'PL',
        changelog: 'CHG',
        sparePart: 'PART',
        equipment: 'ASSET',
        qualityInspection: 'QC',
        lead: 'LED',
        contact: 'CON',
        opportunity: 'OPP',
        campaign: 'CMP',
        interaction: 'INT',
        salesGoal: 'GOL',
        warehouse: 'ENT',
        stockMovement: 'MVT',
        stockReservation: 'RSV',
        transfer: 'TRF',
        adjustment: 'ADJ',
        physicalInventory: 'INV',
        lot: 'LOT',
        valuation: 'VAL',
        replenishment: 'REP',
        purchaseRequest: 'DREQ',
        purchaseOrder: 'CMDF',
        purchaseReceipt: 'REC',
        purchaseReturn: 'RET',
        supplierCatalog: 'SCAT',
        tender: 'AO',
        quoteComparison: 'QCO',
        purchaseBudget: 'BUD',
        accident: 'ACC',
        departure: 'DEP',
        sanction: 'SAN',
        employee: 'EMP',
        leave: 'LEV',
        contract: 'CTR',
        evaluation: 'EVA',
        payrollAdvance: 'AVA',
        payrollLoan: 'LOA',
        payrollTransfer: 'VIR',
        priceRule: 'PRM',
        salesOrder: 'CMD',
        sale: 'VENT',
        delivery: 'LIV',
        salesReturn: 'SR',
        commission: 'COM',
        approvalRequest: 'AR',
        approvalWorkflow: 'WF',
        approvalTemplate: 'TM',
        timeEntry: 'PNT',
        overtime: 'OVT',
        schedule: 'HOR',
        bankAccount: 'BAC',
        bankTransfer: 'TRF',
        bankCheck: 'CHQ',
        bankStatement: 'BST',
        bankEntry: 'BJE',
        journalEntry: 'ECR',
        journalOperation: 'OPJ',
        principalOperation: 'OPM',
        voucher: 'BON',
        closure: 'CLS',
        caisseReconciliation: 'RAP',
        balance: 'BALG',
        grandLivre: 'GRLV',
        bilan: 'BILA',
        accountChart: 'ACC',
        analyticalCode: 'ANL',
        comptabiliteCode: 'COD',
        operationCode: 'OPR',
        taxDeclaration: 'DEC',
      },
      clientDiscounts: {
        company: 0,
        individual: 0,
        institution: 0,
      },
      documentDelays: {
        quote: 15,
        proforma: 30,
        invoice: 30,
        delivery: 0,
        customerReturn: 0,
        creditNote: 0,
        paymentReceipt: 0,
        quoteRequest: 15,
        proformaRequest: 15,
        purchaseOrder: 7,
        supplierReturn: 0,
        paymentVoucher: 0,
      },
      deliveryTimeframes: {
        quoteRealizationDeadline: '',
        quoteDeliveryDelay: '',
        proformaRealizationDeadline: '',
        proformaDeliveryDelay: '',
        quoteRequest: '',
        proformaRequest: '',
        purchaseOrder: '',
      },
    }),
    [],
  );

  const initialSettings = useMemo(
    () => createInitialSettings(defaultConfiguration),
    [defaultConfiguration],
  );

  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const settingsRef = useRef(initialSettings);
  const persistQueueRef = useRef(Promise.resolve());

  const enqueuePersist = useCallback((fn) => {
    const queued = persistQueueRef.current.then(() => fn());
    persistQueueRef.current = queued.catch(() => {});
    return queued;
  }, []);

  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  useEffect(() => {
    let cancelled = false;

    const loadSettings = async () => {
      try {
        const loadedSettings = normalizeSettings(await settingsService.get(), defaultConfiguration);
        if (cancelled) return;
        setSettings(loadedSettings);
        settingsRef.current = loadedSettings;
        cacheSettings(loadedSettings);
      } catch (error) {
        console.error('Error loading settings:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [defaultConfiguration]);

  const refreshData = useCallback(async () => {
    try {
      const loadedSettings = normalizeSettings(await settingsService.get(), defaultConfiguration);
      setSettings(loadedSettings);
      settingsRef.current = loadedSettings;
      cacheSettings(loadedSettings);
    } catch (error) {
      console.error('Error refreshing settings:', error);
    }
  }, [defaultConfiguration]);

  useEffect(() => {
    const handleRefresh = () => {
      refreshData();
    };

    const handleSectionRefresh = (e) => {
      if (e.detail?.section === 'settings') {
        handleRefresh();
      }
    };

    window.addEventListener('refresh-settings', handleRefresh);
    window.addEventListener('refresh-section', handleSectionRefresh);

    return () => {
      window.removeEventListener('refresh-settings', handleRefresh);
      window.removeEventListener('refresh-section', handleSectionRefresh);
    };
  }, [refreshData]);

  const applySettingsPatch = useCallback((updates) => {
    const prev = settingsRef.current;
    if (!prev) return prev;

    const merged = { ...prev };
    Object.entries(updates).forEach(([section, values]) => {
      if (values === undefined) return;
      if (values !== null && typeof values === 'object' && !Array.isArray(values)) {
        merged[section] = { ...(merged[section] || {}), ...values };
      } else {
        merged[section] = values;
      }
    });

    propagateGeneralSettingsToModules(merged, updates);

    settingsRef.current = merged;
    setSettings(merged);
    cacheSettings(merged);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('qawaem-settings-patch', { detail: merged }));
    }

    return merged;
  }, []);

  /** Merge one or more settings sections locally; persist only when requested. */
  const patchSettings = useCallback((updates, { persist = false } = {}) => {
    if (!settingsRef.current) return Promise.resolve();

    if (!persist) {
      applySettingsPatch(updates);
      return Promise.resolve();
    }

    return enqueuePersist(async () => {
      try {
        const merged = applySettingsPatch(updates);
        await settingsService.update(merged);
      } catch (error) {
        console.error('Error persisting settings patch:', error);
      }
    });
  }, [applySettingsPatch, enqueuePersist]);

  const updateSettings = useCallback(async (section, field, value) => {
    if (!settingsRef.current) return;

    return enqueuePersist(async () => {
      try {
        const merged = applySettingsPatch({
          [section]: { [field]: value },
        });
        await settingsService.update(merged);
      } catch (error) {
        console.error(`Error updating setting ${section}.${field}:`, error);
      }
    });
  }, [applySettingsPatch, enqueuePersist]);

  const saveCurrentSettings = useCallback(async () => {
    return enqueuePersist(async () => {
      const current = settingsRef.current;
      if (!current) return;
      await settingsService.update(current);
      cacheSettings(current);
    });
  }, [enqueuePersist]);

  const resetSettings = useCallback(async () => {
    try {
      const defaultSettings = await settingsService.reset();
      const normalized = normalizeSettings(defaultSettings, defaultConfiguration);
      setSettings(normalized);
      settingsRef.current = normalized;
      cacheSettings(normalized);
      return normalized;
    } catch (error) {
      console.error('Error resetting settings:', error);
      throw error;
    }
  }, [defaultConfiguration]);

  const incrementDocumentCounter = useCallback(async (docType) => {
    const snapshot = settingsRef.current;
    if (!snapshot?.invoicing?.documentCounters) {
      return 1;
    }

    let newCounterResolved = 1;

    await enqueuePersist(async () => {
      const prev = settingsRef.current;
      if (!prev?.invoicing?.documentCounters) {
        return;
      }

      const counterMap = {
        invoice: 'invoice',
        proforma: 'proforma',
        'quote-request': 'quoteRequest',
        quote: 'quote',
        'purchase-order': 'purchaseOrder',
        attachment: 'attachment',
        'delivery-note': 'deliveryNote',
        'shipping-slip': 'shippingSlip',
        'reception-note': 'receptionNote',
        'return-slip': 'returnSlip',
        'stock-sheet': 'stockSheet',
        'material-exit': 'materialExit',
        'credit-note': 'creditNote',
        'account-statement': 'accountStatement',
        'advance-invoice': 'advanceInvoice',
        'payment-receipt': 'paymentReceipt',
        'payment-schedule': 'paymentSchedule',
        'commercial-contract': 'commercialContract',
        'service-agreement': 'serviceAgreement',
        'compliance-certificate': 'complianceCertificate',
        'formal-notice': 'formalNotice',
        'reception-report': 'receptionReport',
        'intervention-report': 'interventionReport',
        'non-compliance-sheet': 'nonComplianceSheet',
        'tax-certificate': 'taxCertificate',
        'work-certificate': 'workCertificate',
        'legal-notice': 'legalNotice',
        payslip: 'payslip',
        'work-contract': 'workContract',
        'work-attestation': 'workAttestation',
        'leave-request': 'leaveRequest',
        'production-sheet': 'productionSheet',
        'quality-control': 'qualityControl',
        'manufacturing-order': 'manufacturingOrder',
        'failure-report': 'failureReport',
        'route-sheet': 'routeSheet',
        'delivery-report': 'deliveryReport',
        'transport-order': 'transportOrder',
        'rental-contract': 'rentalContract',
        'property-inspection': 'propertyInspection',
        'rent-receipt': 'rentReceipt',
        'accident-report': 'accidentReport',
        'security-report': 'securityReport',
        'safety-compliance': 'safetyCompliance',
        'maintenance-report': 'maintenanceReport',
        'maintenance-plan': 'maintenancePlan',
        'client-intervention': 'clientIntervention',
        'product-warranty': 'productWarranty',
        'it-audit': 'itAudit',
        'software-install': 'softwareInstall',
        'it-maintenance': 'itMaintenance',
        backup: 'backup',
        'purchase-request': 'purchaseRequest',
        'tender-call': 'tenderCall',
        'supplier-comparison': 'supplierComparison',
        'goods-reception': 'goodsReception',
        'training-plan': 'trainingPlan',
        'training-evaluation': 'trainingEvaluation',
        'training-certificate': 'trainingCertificate',
        'prospect-sheet': 'prospectSheet',
        'client-visit-report': 'clientVisitReport',
        'price-catalog': 'priceCatalog',
        'equipment-sheet': 'equipmentSheet',
        calibration: 'calibration',
        'material-loan': 'materialLoan',
        dashboard: 'dashboard',
        'financial-report': 'financialReport',
        'market-study': 'marketStudy',
        benchmark: 'benchmark',
      };

      const counterKey = counterMap[docType] || docType;
      const currentCounter = prev.invoicing.documentCounters[counterKey] || 1;
      const newCounter = currentCounter + 1;
      newCounterResolved = newCounter;

      const merged = {
        ...prev,
        invoicing: {
          ...prev.invoicing,
          documentCounters: {
            ...prev.invoicing.documentCounters,
            [counterKey]: newCounter,
          },
        },
      };

      settingsRef.current = merged;
      setSettings(merged);
      cacheSettings(merged);
      try {
        await settingsService.update(merged);
      } catch (error) {
        console.error(`Error incrementing counter for ${docType}:`, error);
      }
    });

    return newCounterResolved;
  }, [applySettingsPatch, enqueuePersist]);

  const value = {
    settings,
    loading,
    updateSettings,
    patchSettings,
    saveCurrentSettings,
    resetSettings,
    incrementDocumentCounter,
    refreshData,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
};

export { SettingsContext };
